import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import { toast } from 'react-hot-toast';
import ClockLoader from 'shared/components/ClockLoader';
import { fetchActiveTenantUsers } from 'shared/api/services/tenantUserService';
import { getRegistryTenantIdFromSession, getSessionProfile } from 'shared/utils/sessionProfileStore';
import EntityFormFields from './EntityFormFields';
import { apiErrorMessage } from '../utils/responseHelpers';

function matchCurrentTenantUser(users) {
  const profile = getSessionProfile();
  if (!profile || !users?.length) return null;
  const id = profile.id != null ? String(profile.id) : '';
  const email = String(profile.email || '').trim().toLowerCase();
  return (
    users.find((u) => id && String(u.id) === id)
    || users.find((u) => email && String(u.email || '').trim().toLowerCase() === email)
    || null
  );
}

function AutoFillCurrentUserFields({ fields, lookupOptions, values, setFieldValue }) {
  useEffect(() => {
    const users = lookupOptions?.tenantUsers || [];
    if (!users.length) return;
    const match = matchCurrentTenantUser(users);
    if (!match) return;
    fields.forEach((field) => {
      if (!field.defaultToCurrentUser) return;
      if (values[field.name]) return;
      setFieldValue(field.name, String(match.id));
    });
  }, [fields, lookupOptions, setFieldValue, values]);
  return null;
}

function buildSchema(fields) {
  const shape = {};
  fields.forEach((field) => {
    if (field.hidden || field.hideOnCreate) return;
    if (!field.required) return;
    if (field.type === 'boolean') {
      shape[field.name] = yup.boolean();
    } else if (field.type === 'multiEnum' || field.type === 'multiLookupSelect') {
      shape[field.name] = yup.array().min(1, `${field.label} is required`);
    } else {
      shape[field.name] = yup.string().trim().required(`${field.label} is required`);
    }
  });
  return yup.object(shape);
}

const CreateEntityDialog = ({ open, onClose, onSuccess, config, createItem, softwareOptions }) => {
  const [submitting, setSubmitting] = useState(false);
  const [lookupOptions, setLookupOptions] = useState({});
  const validationSchema = useMemo(() => buildSchema(config.fields), [config.fields]);
  const initialValues = useMemo(
    () => config.getInitialValues(null, config.fields),
    [config]
  );

  useEffect(() => {
    if (!open || !config.loadTenantUsers) {
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const tenantId = getRegistryTenantIdFromSession();
        const users = await fetchActiveTenantUsers(tenantId);
        if (cancelled) return;
        setLookupOptions({
          tenantUsers: users.map((u) => ({
            id: String(u.id),
            name: u.name || u.email || String(u.id),
            email: u.email || '',
            label: u.email ? `${u.name || 'User'} (${u.email})` : (u.name || String(u.id)),
          })),
        });
      } catch {
        if (!cancelled) setLookupOptions({ tenantUsers: [] });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, config.loadTenantUsers]);

  const handleSubmit = async (values, { resetForm }) => {
    if (config.readOnlyMutations) {
      toast.error(`${config.entityName} catalog is read-only.`);
      return;
    }
    setSubmitting(true);
    try {
      await createItem(values);
      toast.success(`${config.entityName} created successfully`);
      resetForm();
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(apiErrorMessage(error, `Failed to create ${config.entityName.toLowerCase()}.`));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ textAlign: 'center', fontWeight: 700 }}>
        Add {config.entityName}
      </DialogTitle>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, errors, touched, handleChange, handleBlur, setFieldValue }) => (
          <Form>
            <AutoFillCurrentUserFields
              fields={config.fields}
              lookupOptions={lookupOptions}
              values={values}
              setFieldValue={setFieldValue}
            />
            <DialogContent>
              <EntityFormFields
                fields={config.fields}
                values={values}
                errors={errors}
                touched={touched}
                handleChange={handleChange}
                handleBlur={handleBlur}
                setFieldValue={setFieldValue}
                softwareOptions={softwareOptions}
                lookupOptions={lookupOptions}
                columns={config.formColumns || 1}
              />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={onClose} disabled={submitting}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" color="secondary" disabled={submitting || config.readOnlyMutations}>
                {submitting ? <ClockLoader inline size={22} /> : 'Create'}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default CreateEntityDialog;
